/**
 * UserChatDock — human-in-the-loop steering chat dock.
 *
 * Floating glass card pinned to the lower-right of the viewport. Lets a
 * human moderator inject `KULLANICI YÖNLENDİRMESİ` messages mid-debate
 * that every persona will see at the start of the next round.
 *
 * Lifecycle states (status === SessionStatus):
 *   • draft / provisioning  → dock collapsed + disabled (no live session)
 *   • running               → fully interactive
 *   • interrupted / error   → input disabled, history visible
 *   • finalized             → input disabled, history kept for context
 *
 * Server is the source-of-truth: every submitted message round-trips
 * through SSE (`user.interject`, status: "queued" → "applied"). Local
 * optimistic insert kept for sub-second feedback only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  ChevronDown,
  CornerDownLeft,
  Loader2,
  MessageSquare,
  Send,
  Sparkles,
} from "lucide-react";

import { useDebateStore } from "../../store/debateStore";
import {
  submitInterjection,
  InterjectError,
} from "../../transport/interjectClient";

const MAX_LEN = 2000;
const SOFT_WARN_AT = 1700;

function formatRelative(ms: number): string {
  const delta = Date.now() - ms;
  if (delta < 5_000) return "az önce";
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s önce`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}d önce`;
  return new Date(ms).toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UserChatDock() {
  const sessionId = useDebateStore((s) => s.sessionId);
  const status = useDebateStore((s) => s.status);
  const interjections = useDebateStore((s) => s.interjections);
  const optimisticInsert = useDebateStore((s) => s.optimisticInsertInterjection);
  const replaceInterjectionId = useDebateStore((s) => s.replaceInterjectionId);
  const removeInterjection = useDebateStore((s) => s.removeInterjection);
  const setPending = useDebateStore((s) => s.setInterjectionPending);
  const setError = useDebateStore((s) => s.setInterjectionError);
  const pending = useDebateStore((s) => s.interjectionPending);
  const error = useDebateStore((s) => s.interjectionError);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  // One-shot guard: auto-open the dock the first time a debate goes
  // live so the operator notices the chat exists. Sticks to the same
  // session id so re-mounting / status oscillation doesn't re-pop it.
  const autoOpenedSession = useRef<string | null>(null);
  const reduceMotion = useReducedMotion();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Hide entirely when there's no debate context to inject into.
  const visible = status === "running"
    || status === "interrupted"
    || (status === "finalized" && interjections.length > 0);

  const interactive = status === "running" && !!sessionId;
  const queuedCount = interjections.filter((m) => m.status === "queued").length;
  const appliedCount = interjections.filter((m) => m.status === "applied").length;

  // First time `running` lands for a brand-new sessionId, pop the dock
  // open so the user discovers the chat capability without hunting for
  // a tiny pill in the corner.
  useEffect(() => {
    if (
      status === "running" &&
      sessionId &&
      autoOpenedSession.current !== sessionId
    ) {
      autoOpenedSession.current = sessionId;
      setOpen(true);
    }
    if (status === "draft") {
      // Reset on a wizard-restart so the next session re-pops cleanly.
      autoOpenedSession.current = null;
    }
  }, [status, sessionId]);

  // Auto-scroll to newest message on update / when opening.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [interjections.length, open]);

  // Auto-grow textarea up to a reasonable cap.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(180, el.scrollHeight)}px`;
  }, [draft, open]);

  const onSubmit = useCallback(async () => {
    if (!sessionId) return;
    const text = draft.trim();
    if (!text) return;
    setPending(true);
    setError(null);

    // Optimistic insert — the SSE echo round-trip from server can take
    // up to ~1 s on a busy backend, and during that time the user's
    // message disappears (input clears) without showing up in the list,
    // creating a strong "did this even go through?" perception.
    //
    // We mint a `local-…` id; the SSE echo will arrive with the
    // server-issued id and the slice's upsert (keyed by id) will keep
    // both records — the local one as a "pending" hint and the server
    // one as the authoritative status. We drop the local one once the
    // server confirms.
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    optimisticInsert({
      type: "user.interject",
      id: localId,
      text,
      receivedAt: Date.now(),
      appliedAtRound: -1,
      status: "queued",
    });
    try {
      const resp = await submitInterjection(sessionId, text);
      // Replace local-id with the server-assigned id so the echo's
      // upsert lands on the same row (no duplicate UI entry).
      replaceInterjectionId(localId, resp.id);
      setDraft("");
    } catch (e) {
      const msg = e instanceof InterjectError
        ? e.message
        : (e instanceof Error ? e.message : "Bilinmeyen hata");
      setError(msg);
      // Rollback the optimistic insert — the message never reached
      // the engine, so leaving it in the list would mislead the user.
      removeInterjection(localId);
    } finally {
      setPending(false);
    }
  }, [
    draft,
    sessionId,
    optimisticInsert,
    replaceInterjectionId,
    removeInterjection,
    setPending,
    setError,
  ]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl+Enter or plain Enter (no Shift) → submit.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!pending && draft.trim()) void onSubmit();
      }
    },
    [draft, pending, onSubmit],
  );

  const helperText = useMemo(() => {
    if (!interactive) {
      if (status === "finalized") return "Tartışma sonlandı — yalnız geçmiş.";
      if (status === "interrupted") return "Akış duraksadı.";
      return "Aktif tartışma yok.";
    }
    if (queuedCount > 0) {
      return `${queuedCount} mesaj bir sonraki turda devreye girecek.`;
    }
    return "Mesajın bir sonraki turun başında her ajana ulaşır.";
  }, [interactive, status, queuedCount]);

  if (!visible) return null;

  return (
    <div
      data-user-chat-dock
      // POSITIONING.
      //
      // Pinned bottom-RIGHT (per UX feedback). Toaster lives top-right
      // by default in the app shell so the two never collide; the
      // right-rail verdict / consensus sidebar terminates ~24rem above
      // viewport bottom, leaving a clean slot for this dock. Z-index 70
      // keeps us above the wizard modal scrim (wizard z = 50) so a
      // queued interjection never gets buried.
      className="pointer-events-none fixed bottom-4 right-4 z-[70] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2"
      aria-live="polite"
    >
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="pointer-events-auto flex w-[22rem] max-w-full flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-surface-1/85 shadow-[0_22px_60px_-15px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:w-[26rem]"
            role="dialog"
            aria-label="Kullanıcı yönlendirme paneli"
          >
            <header className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" aria-hidden />
                  <h3 className="text-sm font-semibold text-fg-strong">
                    Sen de söz al
                  </h3>
                  {/* Konsey Cognitive Renaissance — Bölüm B3.
                      Tek pill "Y/N yansıtıldı" formatı: kullanıcı kaç
                      mesajının uygulandığını + toplamını anlıkta görür.
                      Hiç mesaj yoksa pill'i göster — kullanıcı feature'ın
                      farkında olsun. */}
                  {(appliedCount + queuedCount) > 0 && (
                    <span
                      className={
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide " +
                        (appliedCount > 0
                          ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                          : "border-amber-400/30 bg-amber-400/10 text-amber-300")
                      }
                      title={`${appliedCount} talimat konsey tarafından yansıtıldı, toplam ${appliedCount + queuedCount} talimat`}
                    >
                      <span aria-hidden className="font-semibold">
                        {appliedCount > 0 ? "✓" : "⏳"}
                      </span>
                      <span className="font-mono tabular-nums">
                        {appliedCount}/{appliedCount + queuedCount}
                      </span>
                      <span>yansıtıldı</span>
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-[11px] leading-tight text-fg-subtle">
                  Turlar arası müdahale et, ajanlara öncelikli talimat gönder.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Paneli kapat"
                className="focus-ring -mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-subtle hover:bg-white/[0.06] hover:text-fg-strong"
              >
                <ChevronDown className="h-4 w-4" aria-hidden />
              </button>
            </header>

            <div
              ref={listRef}
              className="max-h-[20rem] min-h-[6rem] flex-1 overflow-y-auto px-4 py-3"
            >
              {interjections.length === 0 ? (
                <p className="py-4 text-center text-xs text-fg-subtle">
                  Henüz mesaj göndermedin. Tartışmayı yönlendirmek için bir
                  konu, soru veya kısıtlama yaz.
                </p>
              ) : (
                <ul className="space-y-2">
                  {interjections.map((m) => {
                    const applied = m.status === "applied";
                    return (
                      <li
                        key={m.id}
                        className={
                          // Two colour lanes: APPLIED (yansıtıldı) →
                          // emerald, makes "this one made it into the
                          // debate" instantly visible. QUEUED (sırada)
                          // → amber, the operator hasn't been heard yet.
                          // The left border is pumped to 4px so even a
                          // glance at the dock conveys per-message
                          // status without reading text.
                          "rounded-xl border-l-4 border border-l-current px-3 py-2 text-[13px] leading-relaxed transition-colors " +
                          (applied
                            ? "border-emerald-400/40 bg-emerald-500/[0.10] text-emerald-300"
                            : "border-amber-400/30 bg-amber-400/[0.06] text-amber-300")
                        }
                      >
                        <div className="mb-1 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide">
                          <span
                            className={
                              "inline-flex items-center gap-1.5 font-semibold " +
                              (applied ? "text-emerald-300" : "text-amber-300")
                            }
                          >
                            {applied ? (
                              <>
                                {/* Discrete check glyph — no external
                                    icon dep needed; renders in any
                                    fontstack. */}
                                <span aria-hidden>✓</span>
                                <span>
                                  Tur {m.appliedAtRound} — yansıtıldı
                                </span>
                              </>
                            ) : (
                              <>
                                <span aria-hidden>⏳</span>
                                <span>Sırada</span>
                              </>
                            )}
                          </span>
                          <span className="text-fg-subtle font-normal">
                            {formatRelative(m.receivedAt)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-fg-strong">
                          {m.text}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="border-t border-white/[0.06] bg-surface-0/50 px-3 py-3">
              {error && (
                <p
                  role="alert"
                  className="mb-2 rounded-md border border-rose-400/30 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200"
                >
                  {error}
                </p>
              )}
              <div className="relative">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
                  onKeyDown={onKeyDown}
                  rows={2}
                  disabled={!interactive || pending}
                  placeholder={
                    interactive
                      ? "Talimatını yaz… (Enter: gönder, Shift+Enter: yeni satır)"
                      : "Tartışma aktif değil"
                  }
                  className="focus-ring w-full resize-none rounded-lg border border-white/10 bg-surface-1/80 px-3 py-2 pr-10 text-sm text-fg-strong placeholder:text-fg-subtle/70 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => void onSubmit()}
                  disabled={!interactive || pending || !draft.trim()}
                  aria-label="Gönder"
                  className="focus-ring absolute bottom-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent text-bg-base disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Send className="h-3.5 w-3.5" aria-hidden />
                  )}
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-fg-subtle">
                <span className="flex items-center gap-1">
                  <CornerDownLeft className="h-3 w-3" aria-hidden />
                  {helperText}
                </span>
                <span
                  className={
                    draft.length >= SOFT_WARN_AT
                      ? "tabular-nums text-amber-300"
                      : "tabular-nums"
                  }
                >
                  {draft.length}/{MAX_LEN}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        {...(reduceMotion
          ? {}
          : { whileHover: { scale: 1.04 }, whileTap: { scale: 0.96 } })}
        className="focus-ring pointer-events-auto relative inline-flex h-12 items-center gap-2 rounded-full border border-white/[0.08] bg-surface-1/90 px-4 text-sm font-medium text-fg-strong shadow-[0_18px_48px_-15px_rgba(0,0,0,0.55)] backdrop-blur-xl transition-colors hover:bg-surface-1/95"
        aria-expanded={open}
        aria-label={open ? "Yönlendirme panelini kapat" : "Yönlendirme paneline söz al"}
      >
        <MessageSquare className="h-4 w-4 text-accent" aria-hidden />
        <span>{open ? "Paneli kapat" : "Sen de söz al"}</span>
        {/* Konsey Cognitive Renaissance — Bölüm B3.
            FAB rozetinde de Y/N format. queued varsa amber, hep applied
            ise emerald — sıfırsa rozet hiç render edilmez. */}
        {(appliedCount + queuedCount) > 0 && (
          <span
            className={
              "ml-1 inline-flex h-5 items-center justify-center rounded-full px-2 font-mono text-[10px] font-semibold tabular-nums " +
              (queuedCount > 0
                ? "bg-amber-400/20 text-amber-200"
                : "bg-emerald-500/25 text-emerald-200")
            }
            title={`${appliedCount}/${appliedCount + queuedCount} talimat yansıtıldı`}
          >
            {appliedCount}/{appliedCount + queuedCount}
          </span>
        )}
      </motion.button>
    </div>
  );
}
